/**
 * donationSuccessUrl — enrichit l'URL de la page /donation-success avec les
 * montants du don (amount / net_amount / include_fees / donate_to_us /
 * interval).
 *
 * Pourquoi : au moment où le donateur est redirigé vers /donation-success,
 * le paiement est DÉJÀ confirmé par Stripe/PayPal, mais la ligne
 * `transactions` n'existe en base qu'à l'arrivée du webhook (quelques
 * secondes plus tard). Sans les montants dans l'URL, la page affichait un
 * squelette gris pendant tout ce temps. Avec eux, `useDonationSuccess`
 * affiche la confirmation instantanément et charge le reçu (ID transaction,
 * organisation, info fiscale) en arrière-plan — pattern identique aux pages
 * de succès Stripe/Revolut.
 *
 * Les montants en query servent UNIQUEMENT à l'affichage optimiste : le reçu
 * (email, PDF, espace donateur) provient toujours du lookup serveur.
 *
 * Utilisé par `useStripeDonationCheckout` et `usePaypalDonationCheckout` —
 * un seul point d'insertion pour les 4 pages de don (/donate, /pay, /c,
 * /campaigns/donate) et tous les moyens de paiement (carte, wallets, PayPal).
 */
import type { Ref, ComputedRef } from "vue";

type NumberSource = Ref<number> | ComputedRef<number>;
type BooleanSource = Ref<boolean> | ComputedRef<boolean>;
type StringSource = Ref<string> | ComputedRef<string>;

export interface DonationAmountSources {
  /** Le donateur couvre-t-il les frais ? (usePaymentForm.includeFees) */
  includeFees: BooleanSource;
  /** Montant net pour l'organisation, en euros (usePaymentCalculations.finalAmount). */
  finalAmount: NumberSource;
  /** Montant total frais inclus, en euros (usePaymentCalculations.amountWithFees). */
  amountWithFees: NumberSource;
  /** Pourboire « Soutenir Donoor » en euros (optionnel). */
  donateToUsAmount?: NumberSource;
  /** Don récurrent en cours ? Ajoute `interval` à l'URL si fourni. */
  isSubscription?: BooleanSource;
  /** Intervalle Stripe ("week" | "month" | "year") si récurrent. */
  interval?: StringSource;
  /**
   * Couleur de l'association (« #RRGGBB »), lue au moment de la redirection :
   * la page de remerciement s'affiche TOUT DE SUITE à ses couleurs, au lieu
   * du bleu Donoor puis de la couleur de l'association une fois le don
   * retrouvé (Karim 21/10 : « au début c'est bleu puis ça devient violet »).
   */
  couleur?: () => string | null | undefined;
}

/** « #RRGGBB » ou « RRGGBB » → « RRGGBB » ; tout le reste → null. */
export function couleurPourUrl(couleur: string | null | undefined): string | null {
  const m = /^#?([0-9a-fA-F]{6})$/.exec((couleur ?? "").trim());
  return m ? m[1]!.toLowerCase() : null;
}

/** Paramètre `couleur` de l'URL → « #rrggbb », ou null s'il est absent ou invalide. */
export function couleurDepuisUrl(valeur: unknown): string | null {
  const hex = typeof valeur === "string" ? couleurPourUrl(valeur) : null;
  return hex ? `#${hex}` : null;
}

/**
 * Wrappe un builder `successUrl` de page pour y ajouter les montants du don.
 * Les valeurs réactives sont lues AU MOMENT de la redirection (post-confirm),
 * donc toujours à jour. Les paramètres déjà présents dans l'URL de base ne
 * sont jamais écrasés.
 */
export function withDonationAmountParams(
  successUrl: (donorEmail?: string) => string,
  sources: DonationAmountSources,
): (donorEmail?: string) => string {
  return (donorEmail?: string) => {
    const base = successUrl(donorEmail);

    const donateToUs = sources.donateToUsAmount?.value || 0;
    // Même règle que buildPaymentBody : donate_to_us TOUJOURS ajouté au
    // montant chargé, même sans include_fees.
    const chargedAmount = sources.includeFees.value
      ? sources.amountWithFees.value
      : sources.finalAmount.value + donateToUs;

    // Montant inconnu ou invalide → URL inchangée (la page retombe sur le
    // squelette + lookup serveur, comportement historique).
    if (!Number.isFinite(chargedAmount) || chargedAmount <= 0) return base;

    const [path, existingQuery = ""] = base.split("?");
    const params = new URLSearchParams(existingQuery);

    if (!params.has("amount")) params.set("amount", String(chargedAmount));
    if (!params.has("net_amount"))
      params.set("net_amount", String(sources.finalAmount.value));
    if (!params.has("include_fees"))
      params.set("include_fees", sources.includeFees.value ? "true" : "false");
    if (!params.has("donate_to_us"))
      params.set("donate_to_us", String(donateToUs));
    if (
      sources.isSubscription?.value &&
      sources.interval?.value &&
      !params.has("interval")
    ) {
      params.set("interval", sources.interval.value);
    }

    const couleur = couleurPourUrl(sources.couleur?.());
    if (couleur && !params.has("couleur")) params.set("couleur", couleur);

    return `${path}?${params.toString()}`;
  };
}
